import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import { copyTextToClipboard } from '../../utils/utils.ts';
import {
  selectSupervisorReadyCovenants,
  selectSupervisorReadyCovenantsError,
  selectSupervisorReadyCovenantsPageConfiguration,
  selectSupervisorReadyCovenantsStatus,
} from '~/store/covenants/selectors.ts';
import { fetchSupervisorApplicationReadyCovenants } from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import {
  MANAGER_READY_COVENANTS_CLOSE_BUTTON_TEXT,
  MANAGER_READY_COVENANTS_COPY_RESET_TIMEOUT_MS,
  MANAGER_READY_COVENANTS_DEFAULT_PAGE_SIZE,
  MANAGER_READY_COVENANTS_DIALOG_TITLE,
  MANAGER_READY_COVENANTS_EMPTY_APPLICATION_TEXT,
  MANAGER_READY_COVENANTS_EMPTY_LIST_TEXT,
} from '../../constants.ts';
import { useManagerReadyCovenantDocuments } from '../../hooks/useManagerReadyCovenantDocuments.ts';
import { ReadyCovenantsList } from './ReadyCovenantsList.tsx';

export interface IManagerReadyCovenantsDialogProps {
  open: boolean;
  onClose: () => void;
  applicationId: string | null;
  pageSize?: number;
}

export const ReadyCovenantsDialog: React.FC<IManagerReadyCovenantsDialogProps> = ({
  open,
  onClose,
  applicationId,
  pageSize = MANAGER_READY_COVENANTS_DEFAULT_PAGE_SIZE,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const covenants = useSelector(selectSupervisorReadyCovenants);
  const pageConfiguration = useSelector(selectSupervisorReadyCovenantsPageConfiguration);
  const status = useSelector(selectSupervisorReadyCovenantsStatus);
  const error = useSelector(selectSupervisorReadyCovenantsError);

  const [page, setPage] = useState(0);
  const [expandedCovenantIndex, setExpandedCovenantIndex] = useState<number | null>(null);
  const [copiedCovenantKey, setCopiedCovenantKey] = useState<string | null>(null);
  const {
    documentsByCovenantKey,
    allDocumentsByCovenantKey,
    documentsLoadingByCovenantKey,
    documentsErrorByCovenantKey,
    handleLoadDocuments,
  } = useManagerReadyCovenantDocuments({
    applicationId,
    open,
  });

  const totalElements = pageConfiguration.totalElements ?? 0;
  const totalPages = Math.ceil(totalElements / pageSize);
  const isLoading = status === FetchStatus.Loading;

  useEffect(() => {
    setPage(0);
    setExpandedCovenantIndex(null);
    setCopiedCovenantKey(null);
  }, [applicationId, open]);

  useEffect(() => {
    if (!open || !applicationId) {
      return;
    }

    dispatch(
      fetchSupervisorApplicationReadyCovenants({
        applicationId,
        page,
        size: pageSize,
      })
    );
  }, [applicationId, dispatch, open, page, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalPages]);

  const toggleCovenant = (index: number) => {
    setExpandedCovenantIndex((previousState) => (previousState === index ? null : index));
  };

  const handleCopyMonitoringId = useCallback(async (covenantKey: string, grafId: number) => {
    const copied = await copyTextToClipboard(String(grafId));

    if (!copied) {
      return;
    }

    setCopiedCovenantKey(covenantKey);
    setTimeout(() => {
      setCopiedCovenantKey((previousState) =>
        previousState === covenantKey ? null : previousState
      );
    }, MANAGER_READY_COVENANTS_COPY_RESET_TIMEOUT_MS);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: managerComponentStyles.readyDialogPaper } }}
      sx={dataFormStyles.dialogSection}
    >
      <DialogTitle>{MANAGER_READY_COVENANTS_DIALOG_TITLE}</DialogTitle>
      <DialogContent dividers sx={managerComponentStyles.readyDialogContent}>
        {!applicationId ? (
          <Typography variant="body2" color="text.secondary">
            {MANAGER_READY_COVENANTS_EMPTY_APPLICATION_TEXT}
          </Typography>
        ) : (
          <>
            {isLoading ? (
              <Box sx={dataFormStyles.dialogLoader}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Typography variant="body2" color="error.main">
                {error.message}
              </Typography>
            ) : !covenants.length ? (
              <Typography variant="body2" color="text.secondary">
                {MANAGER_READY_COVENANTS_EMPTY_LIST_TEXT}
              </Typography>
            ) : (
              <ReadyCovenantsList
                covenants={covenants}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                expandedCovenantIndex={expandedCovenantIndex}
                copiedCovenantKey={copiedCovenantKey}
                documentsByCovenantKey={documentsByCovenantKey}
                allDocumentsByCovenantKey={allDocumentsByCovenantKey}
                documentsLoadingByCovenantKey={documentsLoadingByCovenantKey}
                documentsErrorByCovenantKey={documentsErrorByCovenantKey}
                onToggleCovenant={toggleCovenant}
                onCopyMonitoringId={handleCopyMonitoringId}
                onLoadDocuments={handleLoadDocuments}
                onPageChange={(nextPage) => {
                  setExpandedCovenantIndex(null);
                  setPage(nextPage);
                }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{MANAGER_READY_COVENANTS_CLOSE_BUTTON_TEXT}</Button>
      </DialogActions>
    </Dialog>
  );
};
